import React, { useEffect, useMemo, useState } from 'react';

const FADE_IN_KEYFRAMES = `
@keyframes messageFadeIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
`;

const MESSAGE_FADE_IN_STYLE_ID = 'message-fade-in-animation';

const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersReducedMotion;
};

export type AnimatedMessageRowProps = {
  children: React.ReactNode;
  isNew?: boolean;
  isUser?: boolean;
  style?: React.CSSProperties;
};

export const AnimatedMessageRow = ({
  children,
  isNew,
  isUser = false,
  style: extraStyle
}: AnimatedMessageRowProps) => {
  const rowStyles = useMemo(() => getStyles(), []);
  const baseRowStyle = isUser ? rowStyles.messageRowUser : rowStyles.messageRow;
  const prefersReducedMotion = usePrefersReducedMotion();
  const shouldAnimate = isNew === true && !prefersReducedMotion;
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!document.getElementById(MESSAGE_FADE_IN_STYLE_ID)) {
      const styleEl = document.createElement('style');
      styleEl.id = MESSAGE_FADE_IN_STYLE_ID;
      styleEl.textContent = FADE_IN_KEYFRAMES;
      document.head.appendChild(styleEl);
    }
  }, []);

  const animationStyle: React.CSSProperties =
    shouldAnimate && !animationComplete
      ? {
          animation: 'messageFadeIn 0.4s ease-out forwards'
        }
      : {};

  return (
    <div
      style={{ ...baseRowStyle, ...extraStyle, ...animationStyle }}
      onAnimationEnd={() => setAnimationComplete(true)}
    >
      {children}
    </div>
  );
};

const getStyles = () => ({
  messageRow: {
    display: 'flex',
    alignItems: 'flex-start' as const,
    gap: '12px',
    width: '100%'
  },
  messageRowUser: {
    display: 'flex',
    alignItems: 'flex-start' as const,
    justifyContent: 'flex-end' as const,
    gap: '12px',
    width: '100%'
  }
});
